import { z } from "zod";
import { contentBlobSchema } from "../codec.js";
import { spaceContentSchema } from "./content.js";
import { wrappedKeySchema } from "../spacekey.js";
import {
  discussableTypeSchema,
  inviteKindSchema,
  inviteSchema,
  invitePreviewSchema,
  listItemSchema,
  listKindSchema,
  listSchema,
  orderKeySchema,
  publicUserSchema,
  revisionSchema,
  selfUserSchema,
  spaceMemberSchema,
  spaceRoleSchema,
  spaceSchema,
  spaceSummarySchema,
  timestampSchema,
  uuid,
  placeSchema,
  placeReviewSchema,
  ratingSchema,
  wishSchema,
  wishStatusSchema,
  topicSchema,
  topicStatusSchema,
  messageSchema,
  messageReactionSchema,
  reactionSchema,
  eventSchema,
  eventParticipantSchema,
  eventParticipantStatusSchema,
  MAX_REMINDER_LEAD_MINUTES,
  notificationSchema,
  notificationPrefsSchema,
  notificationTypeSchema,
  tagSchema,
  tagLinkSchema,
  taggableTypeSchema,
  attachmentSchema,
  attachableTypeSchema,
} from "./entities.js";

/**
 * The API contract. Both sides import these: the server validates requests and
 * the client types its calls against them, so a drift between the two is a
 * typecheck failure rather than a runtime surprise.
 */

/* ------------------------------------------------------------------ errors */

export const API_ERROR_CODES = [
  "bad_request",
  "unauthorized",
  "forbidden",
  "not_found",
  "conflict",
  "rate_limited",
  "invite_invalid",
  "invite_expired",
  "invite_used",
  "already_encrypted",
  "email_taken",
  "invalid_credentials",
  "internal",
] as const;

export const apiErrorCodeSchema = z.enum(API_ERROR_CODES);
export type ApiErrorCode = z.infer<typeof apiErrorCodeSchema>;

export const apiErrorSchema = z.object({
  error: z.object({
    code: apiErrorCodeSchema,
    /** Safe to show a person. Never contains internals or field values. */
    message: z.string(),
    fields: z.record(z.string(), z.string()).optional(),
  }),
});
export type ApiError = z.infer<typeof apiErrorSchema>;

/* -------------------------------------------------------------------- auth */

/**
 * Long over complex. Length is the only password rule that reliably survives
 * contact with real users, and there is no password reset in v1 (SPEC §4.1).
 */
export const passwordSchema = z.string().min(10).max(200);

export const displayNameSchema = z.string().trim().min(1).max(60);

export const registerRequestSchema = z.object({
  inviteCode: z.string().trim().min(1).max(32),
  email: z.email().max(254),
  password: passwordSchema,
  displayName: displayNameSchema,
  /**
   * What to call the space registration creates for an `app` invite code.
   *
   * Only read for a code that names no space — a `space` code puts the person
   * into an existing one, which already has a name they do not get to set.
   * Omitted means `DEFAULT_SPACE_NAME`, the same default the bootstrap CLI uses.
   */
  spaceName: spaceContentSchema.shape.name.optional(),
});
export type RegisterRequest = z.infer<typeof registerRequestSchema>;

export const loginRequestSchema = z.object({
  email: z.email().max(254),
  password: z.string().min(1).max(200),
});
export type LoginRequest = z.infer<typeof loginRequestSchema>;

export const sessionResponseSchema = z.object({
  user: selfUserSchema,
  spaces: z.array(spaceSummarySchema),
});
export type SessionResponse = z.infer<typeof sessionResponseSchema>;

/* ----------------------------------------------------------------- profile */

/**
 * What a person may change about themselves (SPEC §4.1).
 *
 * Only the name is here. The avatar arrives as bytes on its own multipart
 * route, and `avatarUrl` is deliberately *not* writable: it is a path the
 * server mints on its own origin, and letting a client set it would turn the
 * member list into an arbitrary image loader pointed at anywhere on the web.
 */
export const updateProfileRequestSchema = z.object({
  displayName: displayNameSchema.optional(),
});
export type UpdateProfileRequest = z.infer<typeof updateProfileRequestSchema>;

export const profileResponseSchema = z.object({ user: selfUserSchema });
export type ProfileResponse = z.infer<typeof profileResponseSchema>;

/* ------------------------------------------------------------------ spaces */

export const createSpaceRequestSchema = z.object({
  content: contentBlobSchema,
});
export type CreateSpaceRequest = z.infer<typeof createSpaceRequestSchema>;

export const updateSpaceRequestSchema = z.object({
  content: contentBlobSchema.optional(),
});
export type UpdateSpaceRequest = z.infer<typeof updateSpaceRequestSchema>;

export const spaceResponseSchema = z.object({ space: spaceSchema });

export const membersResponseSchema = z.object({
  members: z.array(spaceMemberSchema),
});

/* ----------------------------------------------------------------- invites */

/**
 * Mint an invite code.
 *
 * One request shape for both kinds rather than two endpoints: `POST /invites`
 * already existed and was already not nested under a space (the space is named
 * in the body), so a second unnested path would have collided with it, and
 * expiry, single use and the mint rate limit are the same rules either way.
 *
 * `spaceId` is optional here rather than enforced against `kind` by a `.refine`:
 * refining an object schema erases the object type `z.infer` produces, and the
 * client's offline queue types its payloads off exactly these inferred shapes.
 * The route checks the pair instead — `space` needs a `spaceId`, `app` must not
 * carry one.
 */
export const createInviteRequestSchema = z.object({
  kind: inviteKindSchema.default("space"),
  /** Which space the code lets into. Required for `space`, absent for `app`. */
  spaceId: uuid.optional(),
  /** The role inside that space. Ignored for `app`, which always mints an owner. */
  role: spaceRoleSchema.default("member"),
  expiresInHours: z.number().int().min(1).max(24 * 30).default(24 * 7),
});
export type CreateInviteRequest = z.infer<typeof createInviteRequestSchema>;

/**
 * Turning on end-to-end encryption.
 *
 * The only thing sent is a key the server cannot open. There is no passphrase
 * field and there never will be one: the moment a passphrase crosses this
 * boundary, the guarantee the whole scheme exists for is gone.
 */
export const enableEncryptionRequestSchema = z.object({
  wrappedKey: wrappedKeySchema,
});
export type EnableEncryptionRequest = z.infer<typeof enableEncryptionRequestSchema>;

/**
 * Re-encrypting what was written before the space was locked.
 *
 * Turning encryption on does not reach backwards: everything already in the
 * space is a `mode: "none"` blob, and it stays readable to whoever holds the
 * database until it is rewritten. This is the route that rewrites it — and it
 * is deliberately not "update every row", because it must not disturb what the
 * rows mean. An ordinary edit would mark every message as edited and bump every
 * item's `updatedAt`, sorting a year of a shared life by the minute somebody
 * switched encryption on.
 *
 * So the contract is narrow, and the narrowness is the safety:
 *
 *   - only in a space that is already `e2ee`;
 *   - only rows whose stored content is still `mode: "none"`;
 *   - only content is written, never a timestamp, a status or an author;
 *   - anything already encrypted, or gone, is skipped rather than refused.
 *
 * That makes the pass **idempotent and resumable**. A phone that dies halfway
 * leaves the rest in the clear, and running it again finishes the job — which
 * matters, because there is no moment at which the whole space can be assumed
 * to have made it across.
 */
export const encryptableTypes = [
  "space",
  "list",
  "item",
  "wish",
  "message",
  "event",
  "topic",
  "tag",
  "place",
  "review",
] as const;
export const encryptableTypeSchema = z.enum(encryptableTypes);
export type EncryptableType = z.infer<typeof encryptableTypeSchema>;

/** A batch, not the whole space: a pass over a busy space is thousands of rows. */
export const REENCRYPT_BATCH = 200;

export const reencryptRequestSchema = z.object({
  rows: z
    .array(
      z.object({
        type: encryptableTypeSchema,
        id: uuid,
        /** Must be `e2ee`; a plaintext blob here would be a no-op at best. */
        content: contentBlobSchema,
      }),
    )
    .min(1)
    .max(REENCRYPT_BATCH),
});
export type ReencryptRequest = z.infer<typeof reencryptRequestSchema>;

export const reencryptResponseSchema = z.object({
  revision: revisionSchema,
  /** How many rows this call actually rewrote. */
  rewritten: z.number().int().nonnegative(),
  /** Already encrypted, or no longer there. Not an error — see above. */
  skipped: z.number().int().nonnegative(),
});
export type ReencryptResponse = z.infer<typeof reencryptResponseSchema>;

export const inviteResponseSchema = z.object({ invite: inviteSchema });
export const invitePreviewResponseSchema = z.object({
  invite: invitePreviewSchema,
});

/** Joining a space the caller is not yet a member of, while already signed in. */
export const acceptInviteRequestSchema = z.object({
  inviteCode: z.string().trim().min(1).max(32),
});
export type AcceptInviteRequest = z.infer<typeof acceptInviteRequestSchema>;

/* ------------------------------------------------------------------- lists */

export const createListRequestSchema = z.object({
  id: uuid.optional(),
  kind: listKindSchema,
  content: contentBlobSchema,
  position: orderKeySchema.optional(),
});
export type CreateListRequest = z.infer<typeof createListRequestSchema>;

export const updateListRequestSchema = z
  .object({
    content: contentBlobSchema.optional(),
    position: orderKeySchema.optional(),
    archived: z.boolean().optional(),
  })
  .refine((value) => Object.keys(value).length > 0, {
    message: "update must change at least one field",
  });
export type UpdateListRequest = z.infer<typeof updateListRequestSchema>;

export const listResponseSchema = z.object({ list: listSchema });
export const listsResponseSchema = z.object({ lists: z.array(listSchema) });

/* ------------------------------------------------------------------- items */

export const createListItemRequestSchema = z.object({
  /**
   * Client-supplied so an offline create is idempotent: replaying the queued
   * mutation after a flaky reconnect must not produce a second row.
   */
  id: uuid.optional(),
  listId: uuid,
  parentId: uuid.nullable().optional(),
  content: contentBlobSchema,
  position: orderKeySchema.optional(),
  dueAt: timestampSchema.nullable().optional(),
  assigneeId: uuid.nullable().optional(),
});
export type CreateListItemRequest = z.infer<typeof createListItemRequestSchema>;

export const updateListItemRequestSchema = z
  .object({
    content: contentBlobSchema.optional(),
    done: z.boolean().optional(),
    dueAt: timestampSchema.nullable().optional(),
    assigneeId: uuid.nullable().optional(),
    listId: uuid.optional(),
    parentId: uuid.nullable().optional(),
    position: orderKeySchema.optional(),
  })
  .refine((value) => Object.keys(value).length > 0, {
    message: "update must change at least one field",
  });
export type UpdateListItemRequest = z.infer<typeof updateListItemRequestSchema>;

export const listItemResponseSchema = z.object({ item: listItemSchema });
export const listItemsResponseSchema = z.object({
  items: z.array(listItemSchema),
});

/* ------------------------------------------------------------------ wishes */

export const createWishRequestSchema = z.object({
  /** Client-supplied so an offline create replays without duplicating. */
  id: uuid.optional(),
  content: contentBlobSchema,
  position: orderKeySchema.optional(),
  status: wishStatusSchema.default("idea"),
});
export type CreateWishRequest = z.infer<typeof createWishRequestSchema>;

export const updateWishRequestSchema = z
  .object({
    content: contentBlobSchema.optional(),
    status: wishStatusSchema.optional(),
    position: orderKeySchema.optional(),
    /** Marks that one person has taken this on, so the other does not buy it twice. */
    claimed: z.boolean().optional(),
  })
  .refine((value) => Object.keys(value).length > 0, {
    message: "update must change at least one field",
  });
export type UpdateWishRequest = z.infer<typeof updateWishRequestSchema>;

export const wishResponseSchema = z.object({ wish: wishSchema });
export const wishesResponseSchema = z.object({ wishes: z.array(wishSchema) });

/* ------------------------------------------------------------------ places */

export const createPlaceRequestSchema = z.object({
  /** Client-supplied so an offline create replays without duplicating. */
  id: uuid.optional(),
  content: contentBlobSchema,
  /** Set at once when somebody adds a place they have already been to. */
  visited: z.boolean().default(false),
});
export type CreatePlaceRequest = z.infer<typeof createPlaceRequestSchema>;

export const updatePlaceRequestSchema = z
  .object({
    content: contentBlobSchema.optional(),
    /** «Мы тут были» / «ещё нет» — the flag, not a date the client chooses. */
    visited: z.boolean().optional(),
  })
  .refine((value) => Object.keys(value).length > 0, {
    message: "update must change at least one field",
  });
export type UpdatePlaceRequest = z.infer<typeof updatePlaceRequestSchema>;

/**
 * A score, and words if there were any.
 *
 * A PUT rather than a POST: there is one row per person per place, and saying
 * it again is a correction, not a second opinion (see `placeReviewSchema`).
 * `content: null` is «I rated it and wrote nothing», which is different from
 * omitting the field — that would mean «leave what I wrote last time».
 */
export const putPlaceReviewRequestSchema = z.object({
  rating: ratingSchema,
  content: contentBlobSchema.nullable().optional(),
});
export type PutPlaceReviewRequest = z.infer<typeof putPlaceReviewRequestSchema>;

export const placeResponseSchema = z.object({ place: placeSchema });
export const placesResponseSchema = z.object({ places: z.array(placeSchema) });
export const placeReviewResponseSchema = z.object({ review: placeReviewSchema });

/* --------------------------------------------------------------- geocoding */

/**
 * A search for a place by name, forwarded by our server (SPEC §4.10).
 *
 * `near` is a viewbox — west, south, east, north — taken from what the map is
 * already showing, so a search for «пятёрочка» finds the one round the corner
 * first. It is a preference and not a filter, and it says nothing about where
 * the person is: it is where they are looking.
 */
export const geocodeQuerySchema = z.object({
  q: z.string().trim().min(2).max(200),
  near: z
    .string()
    .optional()
    .transform((value) => {
      if (!value) return undefined;
      const parts = value.split(",").map(Number);
      if (parts.length !== 4 || parts.some((part) => !Number.isFinite(part))) {
        return undefined;
      }
      return parts as [number, number, number, number];
    }),
});
export type GeocodeQuery = z.infer<typeof geocodeQuerySchema>;

export const geocodeResultSchema = z.object({
  title: z.string().max(200),
  address: z.string().max(300).optional(),
  lat: z.number().min(-90).max(90),
  lng: z.number().min(-180).max(180),
});
export type GeocodeResult = z.infer<typeof geocodeResultSchema>;

export const geocodeResponseSchema = z.object({
  results: z.array(geocodeResultSchema),
});

/* ------------------------------------------------------------------- chat */

export const createTopicRequestSchema = z.object({
  id: uuid.optional(),
  /** Only threads can be created; the `general` topic is made with the space. */
  kind: z.literal("thread").default("thread"),
  content: contentBlobSchema,
  /**
   * Anchors the discussion to a wish, an item or an event.
   *
   * Both or neither — enforced in the route rather than with a `.refine` here,
   * because wrapping the object in a ZodEffects changes what `z.infer` gives
   * back and the outbox builds its operation types from exactly that.
   */
  targetType: discussableTypeSchema.optional(),
  targetId: uuid.optional(),
});
export type CreateTopicRequest = z.infer<typeof createTopicRequestSchema>;

export const updateTopicRequestSchema = z
  .object({
    content: contentBlobSchema.optional(),
    status: topicStatusSchema.optional(),
    /**
     * Pin to the top of the list, or let it back down among the rest.
     *
     * A boolean rather than a timestamp for the same reason `claimed` is one on
     * a wish: *when* it was pinned is the server's to write, so a client cannot
     * backdate a pin, and unpinning clears the flag and the order together.
     */
    pinned: z.boolean().optional(),
    /**
     * Where it sits among the pinned ones — sent when pinning and when dragging
     * one pin past another. Ignored when `pinned` is false: an unpinned topic
     * has no place in that order.
     */
    pinnedOrder: orderKeySchema.optional(),
  })
  .refine((value) => Object.keys(value).length > 0, {
    message: "update must change at least one field",
  });
export type UpdateTopicRequest = z.infer<typeof updateTopicRequestSchema>;

export const createMessageRequestSchema = z.object({
  id: uuid.optional(),
  topicId: uuid,
  replyToId: uuid.nullable().optional(),
  content: contentBlobSchema,
});
export type CreateMessageRequest = z.infer<typeof createMessageRequestSchema>;

export const updateMessageRequestSchema = z.object({
  content: contentBlobSchema,
});
export type UpdateMessageRequest = z.infer<typeof updateMessageRequestSchema>;

/** How far this person has read, so unread counts mean something. */
export const markReadRequestSchema = z.object({
  messageId: uuid,
});
export type MarkReadRequest = z.infer<typeof markReadRequestSchema>;

export const topicResponseSchema = z.object({ topic: topicSchema });
export const topicsResponseSchema = z.object({ topics: z.array(topicSchema) });
export const messageResponseSchema = z.object({ message: messageSchema });
export const messagesResponseSchema = z.object({
  messages: z.array(messageSchema),
});

/* ---------------------------------------------------------------- unfurl */

export const unfurlRequestSchema = z.object({
  url: z.url().max(2_000),
});
export type UnfurlRequest = z.infer<typeof unfurlRequestSchema>;

/**
 * What the server could read from a page.
 *
 * Every field is optional: plenty of sites block scrapers outright (SPEC §4.3
 * names Instagram, TikTok and Pinterest), and the honest answer then is "just a
 * link", not a guess. The client folds this into the wish's content blob, so an
 * e2ee space keeps its previews — the server sees the page but not what anyone
 * saved about it.
 */
/**
 * The ceiling on a preview picture handed back inside the response.
 *
 * Far below what the fetch itself allows, and deliberately: this one rides in a
 * JSON body, base64 makes it a third larger again, and what it ends up as is a
 * thumbnail on a card. A picture over this is simply left out — a preview
 * without a picture is still a preview (SPEC §4.3), whereas a response nobody
 * can hold in memory is not.
 */
export const INLINE_PREVIEW_IMAGE_MAX_BYTES = 512 * 1024;

export const unfurlResultSchema = z.object({
  /** Where we actually ended up, after any redirects. */
  url: z.string(),
  title: z.string().max(300).optional(),
  description: z.string().max(1_000).optional(),
  siteName: z.string().max(120).optional(),
  price: z.string().max(60).optional(),
  /**
   * A path on this origin, never the original address.
   *
   * Hotlinking would tell the shop every time either person opened their own
   * wish list, and on what device.
   */
  image: z.string().max(300).optional(),
  /**
   * The picture itself, for a space whose files are encrypted.
   *
   * `image` above is a path to bytes this server wrote to its own disk, and in
   * an e2ee space that is a photograph lying in the clear beside the ciphertext
   * everything else in that space became. Fetching still has to happen here —
   * CORS is the whole reason this route exists — but storing does not, so the
   * bytes come back in the response instead and the client seals them and
   * uploads them like any other file. No `media` row is written, which is why
   * this and `image` never both appear.
   */
  inlineImage: z
    .object({
      /** Base64 of at most INLINE_PREVIEW_IMAGE_MAX_BYTES bytes. */
      data: z.string().max(Math.ceil(INLINE_PREVIEW_IMAGE_MAX_BYTES / 3) * 4),
      /** What the picture really is — checked against its bytes before this. */
      mime: z.string().max(100),
    })
    .optional(),
});
export type UnfurlResult = z.infer<typeof unfurlResultSchema>;

export const unfurlResponseSchema = z.object({ preview: unfurlResultSchema });

/* --------------------------------------------------------------- calendar */

export const createEventRequestSchema = z.object({
  id: uuid.optional(),
  content: contentBlobSchema,
  startsAt: timestampSchema,
  endsAt: timestampSchema.nullable().optional(),
  allDay: z.boolean().default(false),
  rrule: z.string().max(500).nullable().optional(),
  /** Notice for this event alone. Absent or null means "as usual" (§4.7). */
  reminderLeadMinutes: z
    .number()
    .int()
    .min(0)
    .max(MAX_REMINDER_LEAD_MINUTES)
    .nullable()
    .optional(),
});
export type CreateEventRequest = z.infer<typeof createEventRequestSchema>;

export const updateEventRequestSchema = z
  .object({
    content: contentBlobSchema.optional(),
    startsAt: timestampSchema.optional(),
    endsAt: timestampSchema.nullable().optional(),
    allDay: z.boolean().optional(),
    rrule: z.string().max(500).nullable().optional(),
    reminderLeadMinutes: z
      .number()
      .int()
      .min(0)
      .max(MAX_REMINDER_LEAD_MINUTES)
      .nullable()
      .optional(),
  })
  .refine((value) => Object.keys(value).length > 0, {
    message: "update must change at least one field",
  });
export type UpdateEventRequest = z.infer<typeof updateEventRequestSchema>;

export const eventResponseSchema = z.object({ event: eventSchema });
export const eventsResponseSchema = z.object({ events: z.array(eventSchema) });

/**
 * Say whether *you* are coming.
 *
 * There is no `userId` in this body, and that absence is the whole design: the
 * route is `…/participants/me` and the person is taken from the session, so
 * there is no field a client could fill in with somebody else's id. Marking a
 * partner as "not going" is not a thing this API can express (SPEC §4.4).
 *
 * A bare object with one enum — deliberately not wrapped in a `.refine`, like
 * every other request shape here: refining an object turns it into a ZodEffects
 * and `z.infer` stops giving back an object type, which is exactly what the
 * client's offline queue builds its operation payloads from.
 */
export const setEventParticipationRequestSchema = z.object({
  status: eventParticipantStatusSchema,
});
export type SetEventParticipationRequest = z.infer<
  typeof setEventParticipationRequestSchema
>;

/**
 * React to a message, or take the reaction back.
 *
 * `null` is "no longer any of them", and it is a value rather than a separate
 * route because tapping the face you already gave is how a person takes it
 * back — one gesture, one operation, and nothing to coalesce wrongly against.
 *
 * No `userId`, for the same reason `…/participants/me` has none: the route
 * names `me` and the server reads the person off the session, so this API
 * cannot express reacting on somebody else's behalf.
 */
export const setReactionRequestSchema = z.object({
  emoji: reactionSchema.nullable(),
});
export type SetReactionRequest = z.infer<typeof setReactionRequestSchema>;

/**
 * `null` when there is nothing to describe: a reaction taken back that was
 * never given. Fabricating a row to fill the field would be the API lying about
 * a row that does not exist.
 */
export const messageReactionResponseSchema = z.object({
  reaction: messageReactionSchema.nullable(),
});
export const messageReactionsResponseSchema = z.object({
  reactions: z.array(messageReactionSchema),
});

export const eventParticipantResponseSchema = z.object({
  participant: eventParticipantSchema,
});
export const eventParticipantsResponseSchema = z.object({
  participants: z.array(eventParticipantSchema),
});

/**
 * Carry one event's files and its answers onto another.
 *
 * There is one thing this exists for: editing a single day of a series splits
 * it, and the copy that takes that day's place inherits the form's fields and
 * nothing else — so the tickets, the photo of the invitation and «иду» / «не
 * иду» would stay behind on the series and the evening would arrive stripped.
 *
 * It has to be the server that does it. The bytes of an attachment are not on
 * the device that is splitting the day, so a client could only carry a file
 * over by uploading it a second time — a second copy of the same picture, and
 * only if it happened to have it.
 *
 * The event being written to is the one in the path; `fromEventId` is where the
 * two are read from. Both must be in the same space, which is the only thing
 * this body can say — there is no field for a target type or a space, because
 * this is not a general "copy things between rows" door.
 */
export const inheritEventDetailsRequestSchema = z.object({
  fromEventId: uuid,
});
export type InheritEventDetailsRequest = z.infer<
  typeof inheritEventDetailsRequestSchema
>;

/**
 * How much was actually carried over — files, then answers.
 *
 * Counts rather than the rows themselves: everything written here reaches the
 * caller through the delta it just triggered, and answering with the rows a
 * second time would give a client two ways to learn the same fact. Both are
 * zero for a repeat call, which is how one is told apart from the first.
 */
export const inheritEventDetailsResponseSchema = z.object({
  attachments: z.number().int().nonnegative(),
  participants: z.number().int().nonnegative(),
});
export type InheritEventDetailsResponse = z.infer<
  typeof inheritEventDetailsResponseSchema
>;

/* ------------------------------------------------------------------- tags */

export const createTagRequestSchema = z.object({
  id: uuid.optional(),
  content: contentBlobSchema,
  color: z.string().max(24).nullable().optional(),
});
export type CreateTagRequest = z.infer<typeof createTagRequestSchema>;

export const updateTagRequestSchema = z
  .object({
    content: contentBlobSchema.optional(),
    color: z.string().max(24).nullable().optional(),
  })
  .refine((value) => Object.keys(value).length > 0, {
    message: "update must change at least one field",
  });
export type UpdateTagRequest = z.infer<typeof updateTagRequestSchema>;

export const tagLinkRequestSchema = z.object({
  tagId: uuid,
  targetType: taggableTypeSchema,
  targetId: uuid,
});
export type TagLinkRequest = z.infer<typeof tagLinkRequestSchema>;

export const tagResponseSchema = z.object({ tag: tagSchema });
export const tagsResponseSchema = z.object({ tags: z.array(tagSchema) });

/* ------------------------------------------------------------ attachments */

/**
 * The bytes go up as `multipart/form-data`; this is the metadata beside them.
 *
 * `content` is a blob like any other typed text, so a filename and caption stay
 * unreadable to the server in an encrypted space. The bytes themselves are
 * opaque to it either way — and when e2ee lands they will be encrypted before
 * they leave the device, with nothing here needing to change.
 */
export const createAttachmentFieldsSchema = z.object({
  id: uuid.optional(),
  targetType: attachableTypeSchema,
  targetId: uuid,
  content: contentBlobSchema,
});
export type CreateAttachmentFields = z.infer<typeof createAttachmentFieldsSchema>;

export const attachmentResponseSchema = z.object({ attachment: attachmentSchema });
export const attachmentsResponseSchema = z.object({
  attachments: z.array(attachmentSchema),
});

/* --------------------------------------------------------- sealing files */

/**
 * How many files one "what is left" call may name.
 *
 * The same shape of answer `REENCRYPT_BATCH` gives for rows, and for the same
 * reason: the pass is a loop the client runs, one batch at a time, and it has to
 * survive being closed halfway and started again from whichever device is at
 * hand.
 */
/** Where a sealed file landed. A path on our own origin, never anywhere else. */
export const sealedMediaResponseSchema = z.object({ url: z.string().startsWith("/") });
export type SealedMediaResponse = z.infer<typeof sealedMediaResponseSchema>;

export const UNSEALED_MEDIA_BATCH = 200;

/**
 * The files of a space that turning encryption on did not reach.
 *
 * Ids only — the bytes come from `/api/media/:id` as they always have. `more`
 * says another batch is waiting, so a client can show how far it has got
 * without the server having to count what it has not looked at yet.
 */
export const unsealedMediaResponseSchema = z.object({
  mediaIds: z.array(z.string().max(200)).max(UNSEALED_MEDIA_BATCH),
  more: z.boolean(),
});
export type UnsealedMediaResponse = z.infer<typeof unsealedMediaResponseSchema>;

export const sealMediaResponseSchema = z.object({
  /** Unchanged by the sealing: everything already points at this id. */
  mediaId: z.string().max(200),
  /**
   * `skipped` means the file was an envelope already. Not an error — the pass is
   * meant to be run again, and a second run of a finished one must be quiet.
   */
  status: z.enum(["sealed", "skipped"]),
});
export type SealMediaResponse = z.infer<typeof sealMediaResponseSchema>;

/**
 * The name a stored file goes by: the salted hash of its bytes and the
 * extension it was stored under (apps/api/src/lib/media-id.ts).
 *
 * Spelled out rather than left a loose string because the id is also the file's
 * name on the disk, and the route that lets a file go takes it from the caller.
 */
export const mediaIdSchema = z
  .string()
  .regex(/^[0-9a-f]{64}\.[a-z0-9]{1,8}$/, "not a media id");

/**
 * What became of a file a client asked the space to stop keeping.
 *
 * `kept` is not a refusal and not an error: something else in the space — an
 * attachment somebody hung on a wish — is still showing those bytes, and the
 * client that asked has no way of seeing that from where it stands. `released`
 * means the space keeps them no longer, which is also the answer for a file
 * that was already gone: this is called after the thing that referred to the
 * file has been deleted, so a second attempt has to be quiet rather than loud.
 */
export const releaseMediaResponseSchema = z.object({
  mediaId: mediaIdSchema,
  status: z.enum(["released", "kept"]),
});
export type ReleaseMediaResponse = z.infer<typeof releaseMediaResponseSchema>;

/* ------------------------------------------------------------------- push */

/**
 * A browser's push subscription, as `PushManager.subscribe` hands it over.
 *
 * The keys belong to this browser on this device; the server encrypts each
 * payload with them per RFC 8291, so the push service in the middle carries
 * something it cannot read.
 */
export const pushSubscriptionSchema = z.object({
  endpoint: z.url().max(1_000),
  keys: z.object({
    p256dh: z.string().min(1).max(200),
    auth: z.string().min(1).max(100),
  }),
});
export type PushSubscriptionInput = z.infer<typeof pushSubscriptionSchema>;

export const subscribePushRequestSchema = z.object({
  subscription: pushSubscriptionSchema,
});

export const unsubscribePushRequestSchema = z.object({
  endpoint: z.url().max(1_000),
});

export const pushKeyResponseSchema = z.object({
  /** The VAPID public key, which the browser needs before it can subscribe. */
  publicKey: z.string().min(1),
});

export const updateNotificationPrefsRequestSchema = notificationPrefsSchema.partial().refine(
  (value) => Object.keys(value).length > 0,
  { message: "update must change at least one field" },
);
export type UpdateNotificationPrefsRequest = z.infer<
  typeof updateNotificationPrefsRequestSchema
>;

export const notificationPrefsResponseSchema = z.object({
  prefs: notificationPrefsSchema,
});

export const notificationsResponseSchema = z.object({
  notifications: z.array(notificationSchema),
});

/**
 * Asked for alongside a mutation, so the other person hears about it.
 *
 * `preview` is written and encrypted by the client that acted — the server
 * forwards it untouched. That is the whole reason a push can carry real text in
 * an encrypted space (SPEC §3, decision 3).
 */
export const notifyRequestSchema = z.object({
  type: notificationTypeSchema,
  targetType: z.string().max(40).optional(),
  targetId: uuid.optional(),
  preview: contentBlobSchema,
  /**
   * Who it is for, when it is for one person rather than the room.
   *
   * "Вам назначено дело" said to everybody is true for one of them and a lie to
   * the rest — harmless in a space of two, wrong the moment there are three.
   * Absent means everyone but the sender, which is right for a new message or a
   * new wish: those really are addressed to the room.
   */
  userId: uuid.optional(),
});
export type NotifyRequest = z.infer<typeof notifyRequestSchema>;

/**
 * What the service worker receives.
 *
 * Deliberately small: a Web Push payload caps at 4KB after encryption and base64
 * inflates it by a third, so this carries a short summary and identifiers,
 * never a message body or a picture.
 */
export const pushPayloadSchema = z.object({
  v: z.literal(1),
  spaceId: uuid,
  type: notificationTypeSchema,
  notificationId: uuid,
  targetType: z.string().max(40).nullable(),
  targetId: uuid.nullable(),
  preview: contentBlobSchema.nullable(),
  /**
   * The moment this is *about* — the occurrence starting, the deadline falling.
   *
   * Only a reminder has one, and it is here because the server may say it
   * without opening anything: `starts_at` and `due_at` are columns precisely so
   * a background pass can know *when* without knowing *what* (SPEC §3). The
   * word «во сколько» was the half of «скоро событие» the server could always
   * have answered and never did.
   */
  at: timestampSchema.nullable().default(null),
  /**
   * Who did it, by name, for the notifications the server composes on its own.
   *
   * A display name is a plaintext column and one of §3's accepted leaks, so
   * this opens nothing. A name and not a sentence: the app reads in two
   * languages, and the wording belongs to the worker that shows it.
   */
  actorName: z.string().max(60).nullable().default(null),
});
export type PushPayload = z.infer<typeof pushPayloadSchema>;

/* -------------------------------------------------------------------- sync */

export const syncQuerySchema = z.object({
  since: z.coerce.number().int().nonnegative().default(0),
});
export type SyncQuery = z.infer<typeof syncQuerySchema>;

/**
 * Everything in the space that changed after `since`, plus the space's current
 * revision so the client knows where to resume. Deleted rows are present with
 * `deletedAt` set — a client that missed the delete would otherwise keep the row
 * forever.
 */
export const syncDeltaSchema = z.object({
  revision: revisionSchema,
  /**
   * True when the server could not serve an incremental delta and sent the full
   * state instead. The client must replace rather than merge.
   */
  reset: z.boolean(),
  space: spaceSchema,
  members: z.array(spaceMemberSchema),
  lists: z.array(listSchema),
  items: z.array(listItemSchema),
  wishes: z.array(wishSchema),
  places: z.array(placeSchema),
  /**
   * Who thought what of which place.
   *
   * In the delta rather than fetched per place, like the event answers and the
   * reactions below it: the map draws every pin at once, and a pin has to know
   * its score before it is drawn.
   */
  placeReviews: z.array(placeReviewSchema),
  topics: z.array(topicSchema),
  /**
   * Every message in the space on a reset.
   *
   * Fine for two people and a year of use; if a space ever accumulates enough
   * that a cold start feels heavy, this is the field that needs paging, not the
   * rest of the delta.
   */
  messages: z.array(messageSchema),
  events: z.array(eventSchema),
  /**
   * Who answered what about which event.
   *
   * In the delta like everything else, rather than fetched per event when a
   * sheet opens: the agenda wants to show "оба идут" on a row without a request
   * per row, and an answer given on the other device has to arrive the same way
   * a moved event does.
   */
  eventParticipants: z.array(eventParticipantSchema),
  /**
   * Who answered which message without words.
   *
   * In the delta rather than fetched per message, exactly like the event
   * answers above: a conversation draws a screenful of bubbles at once, and a
   * request per bubble is not a thing to do on a phone.
   */
  reactions: z.array(messageReactionSchema),
  tags: z.array(tagSchema),
  tagLinks: z.array(tagLinkSchema),
  attachments: z.array(attachmentSchema),
});
export type SyncDelta = z.infer<typeof syncDeltaSchema>;

/* --------------------------------------------------------------- websocket */

export const serverMessageSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("hello"),
    userId: uuid,
    spaces: z.array(z.object({ spaceId: uuid, revision: revisionSchema })),
  }),
  /**
   * Deliberately carries no content — just a nudge to pull the delta. Keeps one
   * code path for "I was offline" and "something just changed", and keeps the
   * socket useless to anyone who cannot already read the space.
   */
  z.object({
    type: z.literal("changed"),
    spaceId: uuid,
    revision: revisionSchema,
    actorId: uuid.nullable(),
  }),
  z.object({ type: z.literal("pong") }),
]);
export type ServerMessage = z.infer<typeof serverMessageSchema>;

export const clientMessageSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("subscribe"), spaceId: uuid }),
  z.object({ type: z.literal("unsubscribe"), spaceId: uuid }),
  z.object({ type: z.literal("ping") }),
]);
export type ClientMessage = z.infer<typeof clientMessageSchema>;

/* ------------------------------------------------------------------ people */

export const usersResponseSchema = z.object({
  users: z.array(publicUserSchema),
});
